"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useEffect, useRef, useState } from "react";
import { getComponent } from "../../../../client";
import { AbyssAction } from "../AbyssAction/AbyssAction";
import { AbyssMedia } from "../AbyssMedia/AbyssMedia";
import type { IMegaMenuNavigationProps } from "./MegaMenuNavigation.props";

export const MegaMenuNavigation = ({ actions, items, logo, sticky }: IMegaMenuNavigationProps) => {
  const Link = getComponent("Link");
  const [openDesktop, setOpenDesktop] = useState<number | null>(null);
  const [openMobile, setOpenMobile] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const headerRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const desktopTriggerRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
    const updatePath = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", updatePath);
    return () => window.removeEventListener("popstate", updatePath);
  }, []);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenDesktop(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || openDesktop === null) return;
      desktopTriggerRefs.current[openDesktop]?.focus();
      setOpenDesktop(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [openDesktop]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (drawerOpen && !dialog.open) dialog.showModal();
    if (!drawerOpen && dialog.open) dialog.close();
  }, [drawerOpen]);
  const navigate = () => {
    setDrawerOpen(false);
    setOpenMobile(null);
  };
  const linkClass =
    "inline-flex min-h-11 items-center px-3 font-abyss-display text-sm font-medium text-foreground transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
  const isCurrentPath = (href: string) =>
    href.startsWith("/") &&
    (currentPath === href || (href !== "/" && currentPath.startsWith(`${href}/`)));
  const nestedLink = (
    link: {
      accessibilityLabel?: string | null;
      href: string;
      label: string;
      openInNewTab?: boolean | null;
    },
    key: number,
    mobile = false,
  ) => (
    <Link
      aria-current={isCurrentPath(link.href) ? "page" : undefined}
      aria-label={link.accessibilityLabel || undefined}
      className={cn(
        linkClass,
        "w-full",
        isCurrentPath(link.href) && "bg-surface-200 text-cyan-trace",
      )}
      href={link.href}
      key={key}
      onClick={() => {
        if (link.href.startsWith("/"))
          setCurrentPath(new URL(link.href, window.location.href).pathname);
        if (mobile) navigate();
      }}
      rel={link.openInNewTab ? "noopener noreferrer" : undefined}
      target={link.openInNewTab ? "_blank" : undefined}
    >
      {link.label}
    </Link>
  );

  if (!logo && !items?.length && !actions?.length) return null;

  return (
    <header
      className={cn(
        "z-40 border-b border-iron bg-absolute-black/95 backdrop-blur-md",
        sticky && "sticky top-0",
      )}
      ref={headerRef}
    >
      <div className="abyss-container flex min-h-20 items-center gap-6">
        {logo ? (
          <Link
            aria-label="Home"
            className="shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            href="/"
          >
            <AbyssMedia
              className="h-11 w-48"
              frameClassName="bg-transparent"
              imageClassName="object-contain object-left"
              media={logo}
              sizes="192px"
            />
          </Link>
        ) : null}
        <nav aria-label="Primary" className="ml-auto hidden items-center gap-1 lg:flex">
          {items?.map((item, index) =>
            item.blockType === "megaMenuDirectLink" ? (
              nestedLink(item.link, index)
            ) : (
              <div className="relative" key={index}>
                <button
                  aria-expanded={openDesktop === index}
                  className={cn(
                    linkClass,
                    "cursor-pointer gap-2",
                    (item.blockType === "megaMenuDropdown"
                      ? item.links
                      : item.groupItems.flatMap((group) => group.links)
                    ).some((link) => isCurrentPath(link.href)) && "text-cyan-trace",
                  )}
                  onClick={() => setOpenDesktop(openDesktop === index ? null : index)}
                  ref={(element) => {
                    desktopTriggerRefs.current[index] = element;
                  }}
                  type="button"
                >
                  {item.label}
                  <Icon
                    className={cn(
                      "size-4 shrink-0 text-cyan-trace transition-transform duration-200",
                      openDesktop === index && "rotate-180",
                    )}
                    name="chevron-down"
                  />
                </button>
                {openDesktop === index ? (
                  <div
                    className={cn(
                      "absolute right-0 top-full z-50 mt-3 min-w-64 rounded-xl border border-iron bg-obsidian p-2 shadow-[0_24px_70px_rgba(0,0,0,0.48)]",
                      item.blockType === "megaMenuGroup" &&
                        "grid min-w-[36rem] grid-cols-2 gap-4 p-4",
                    )}
                  >
                    {item.blockType === "megaMenuDropdown"
                      ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex))
                      : item.groupItems.map((group, groupIndex) => (
                          <div key={groupIndex}>
                            <p className="border-b border-iron px-3 py-2 font-abyss-mono text-sm text-primary">
                              {group.header}
                            </p>
                            {group.links.map((link, linkIndex) => nestedLink(link, linkIndex))}
                          </div>
                        ))}
                  </div>
                ) : null}
              </div>
            ),
          )}
        </nav>
        {actions?.length ? (
          <div className="hidden gap-2 lg:flex">
            {actions.map((action, index) => (
              <AbyssAction action={action} key={index} />
            ))}
          </div>
        ) : null}
        <button
          aria-controls="abyss-mobile-navigation"
          aria-expanded={drawerOpen}
          aria-haspopup="dialog"
          aria-label={drawerOpen ? "Close navigation menu" : "Open navigation menu"}
          className="ml-auto inline-flex size-11 items-center justify-center rounded-lg border border-iron focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
          onClick={() => setDrawerOpen(true)}
          type="button"
        >
          <span className="font-abyss-mono text-sm">Menu</span>
        </button>
      </div>
      <dialog
        className="m-0 ml-auto h-dvh max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-absolute-black/45 md:w-[28rem]"
        id="abyss-mobile-navigation"
        onClose={() => setDrawerOpen(false)}
        ref={dialogRef}
      >
        <div className="flex h-full w-full flex-col border-l border-iron bg-obsidian p-6 shadow-[-24px_0_70px_rgba(0,0,0,0.45)]">
          <div className="flex items-center justify-between border-b border-iron pb-4">
            {logo ? (
              <Link
                aria-label="Home"
                className="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                href="/"
                onClick={navigate}
              >
                <AbyssMedia
                  className="h-10 w-44"
                  frameClassName="bg-transparent"
                  imageClassName="object-contain object-left"
                  media={logo}
                  sizes="176px"
                />
              </Link>
            ) : (
              <span className="font-abyss-mono text-sm">Menu</span>
            )}
            <button
              aria-label="Close navigation menu"
              className="inline-flex size-11 items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={() => setDrawerOpen(false)}
              type="button"
            >
              <span className="font-abyss-mono text-sm">Close</span>
            </button>
          </div>
          <nav aria-label="Mobile primary" className="flex-1 overflow-y-auto py-4">
            {items?.map((item, index) =>
              item.blockType === "megaMenuDirectLink" ? (
                nestedLink(item.link, index, true)
              ) : (
                <div className="border-b border-iron" key={index}>
                  <button
                    aria-expanded={openMobile === index}
                    className={cn(linkClass, "w-full cursor-pointer justify-between")}
                    onClick={() => setOpenMobile(openMobile === index ? null : index)}
                    type="button"
                  >
                    {item.label}
                    <Icon
                      className={cn(
                        "size-4 shrink-0 text-cyan-trace transition-transform duration-200",
                        openMobile === index && "rotate-180",
                      )}
                      name="chevron-down"
                    />
                  </button>
                  {openMobile === index ? (
                    <div className="pb-3 pl-3">
                      {item.blockType === "megaMenuDropdown"
                        ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex, true))
                        : item.groupItems.map((group, groupIndex) => (
                            <div key={groupIndex}>
                              <p className="px-3 py-2 font-abyss-mono text-sm text-primary">
                                {group.header}
                              </p>
                              {group.links.map((link, linkIndex) =>
                                nestedLink(link, linkIndex, true),
                              )}
                            </div>
                          ))}
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </nav>
          {actions?.length ? (
            <div className="flex flex-col gap-2 border-t border-iron pt-4">
              {actions.map((action, index) => (
                <AbyssAction action={action} key={index} />
              ))}
            </div>
          ) : null}
        </div>
      </dialog>
    </header>
  );
};

MegaMenuNavigation.displayName = "Content::MegaMenuNavigation";
